import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import useLocalizedMarketingPath from '../../hooks/useLocalizedMarketingPath';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import { BRAND } from '../../theme/brandTokens';
import { brandBackdropSx, brandCardSx } from '../../theme/brandSurfaces';

const REDIRECT_SECONDS = 5;

const NotFoundPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const homePath = useLocalizedMarketingPath()('/');
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    const timeoutId = setTimeout(() => {
      navigate(homePath, { replace: true });
    }, REDIRECT_SECONDS * 1000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [navigate, homePath]);

  const handleGoHome = () => {
    navigate(homePath, { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...brandBackdropSx,
        p: 3,
        position: 'relative',
      }}
    >
      <Helmet>
        <title>{`${t('notFound.title')} | Salovare`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Box sx={{ position: 'absolute', top: 24, right: 24, zIndex: 1100 }}>
        <LanguageSwitcher sx={{ mb: 0 }} />
      </Box>
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          maxWidth: 500,
          ...brandCardSx,
        }}
      >
        <Typography variant="h1" component="h1" sx={{ fontSize: '6rem', fontWeight: 800, color: BRAND.accentText, mb: 2 }}>
          404
        </Typography>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
          {t('notFound.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('notFound.message')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
          {t('notFound.suggestion')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('notFound.contactStore')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('notFound.redirectCountdown', { seconds: secondsLeft })}
        </Typography>
        <PrimaryActionButton onClick={handleGoHome} fullWidth>
          {t('notFound.goToHome')}
        </PrimaryActionButton>
      </Paper>
    </Box>
  );
};

export default NotFoundPage;
